import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { ChevronDown, ChevronUp, AlertOctagon, AlertTriangle, ShieldCheck } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

interface Risk {
  category: string;
  description: string;
  severity: string;
  mitigation?: string;
}

interface RiskAlertsSectionProps {
  risks: Risk[];
  /** When true, render all risks expanded with no chevron / preview / toggle.
   *  Use when the parent already provides a collapse layer. */
  alwaysExpanded?: boolean;
}

/**
 * Color logic — severity drives the visual prominence, mitigation softens it:
 *  - High + unmitigated: solid red bg, white text
 *  - High + mitigated:   red border, white bg, red text
 *  - Medium + unmitigated: amber border, amber-tinted bg
 *  - Medium + mitigated:  amber border, white bg
 *  - Low: neutral border, white bg
 */
function getRiskStyle(severity: string, mitigated: boolean, colors: any) {
  const isHigh = severity === 'high';
  const isMed = severity === 'medium';

  if (isHigh && !mitigated) {
    return {
      bg: colors.error,
      border: colors.error,
      text: '#ffffff',
      iconColor: '#ffffff',
      Icon: AlertOctagon,
    };
  }
  if (isHigh && mitigated) {
    return {
      bg: colors.surface.primary,
      border: colors.error,
      text: colors.error,
      iconColor: colors.error,
      Icon: AlertOctagon,
    };
  }
  if (isMed && !mitigated) {
    return {
      bg: colors.amber.light,
      border: colors.warning,
      text: colors.text.primary,
      iconColor: colors.warning,
      Icon: AlertTriangle,
    };
  }
  if (isMed && mitigated) {
    return {
      bg: colors.surface.primary,
      border: colors.warning,
      text: colors.text.primary,
      iconColor: colors.warning,
      Icon: AlertTriangle,
    };
  }
  return {
    bg: colors.surface.primary,
    border: colors.border.medium,
    text: colors.text.secondary,
    iconColor: colors.text.tertiary,
    Icon: AlertTriangle,
  };
}

function RiskCard({ risk, colors }: { risk: Risk; colors: any }) {
  const mitigated = !!risk.mitigation;
  const style = getRiskStyle(risk.severity, mitigated, colors);
  const Icon = style.Icon;

  return (
    <View
      style={{
        backgroundColor: style.bg,
        borderWidth: 1.5,
        borderColor: style.border,
        borderRadius: 10,
        padding: 12,
        marginBottom: 8,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <Icon size={15} color={style.iconColor} style={{ marginTop: 2 }} />
        <View style={{ flex: 1 }}>
          <Text
            style={{
              color: style.text,
              fontSize: 13,
              lineHeight: 18,
              fontWeight: '600',
            }}
          >
            {risk.description}
          </Text>
          {mitigated && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 6,
                marginTop: 8,
                paddingTop: 8,
                borderTopWidth: 1,
                borderTopColor:
                  risk.severity === 'high' && !mitigated
                    ? 'rgba(255,255,255,0.2)'
                    : colors.border.light,
              }}
            >
              <ShieldCheck size={12} color={colors.success} style={{ marginTop: 2 }} />
              <View style={{ flex: 1 }}>
                <Text
                  style={{
                    color: colors.success,
                    fontSize: 9,
                    fontWeight: '700',
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}
                >
                  Mitigation
                </Text>
                <Text
                  style={{
                    color: colors.text.secondary,
                    fontSize: 11,
                    lineHeight: 15,
                  }}
                >
                  {risk.mitigation}
                </Text>
              </View>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

export function RiskAlertsSection({ risks, alwaysExpanded = false }: RiskAlertsSectionProps) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState<boolean>(false);

  if (risks.length === 0) return null;

  // Sort: high unmitigated first, then high mitigated, then medium unmitigated, then rest
  const sorted = [...risks].sort((a, b) => {
    const score = (r: Risk) => {
      const sev = r.severity === 'high' ? 0 : r.severity === 'medium' ? 2 : 4;
      const mit = r.mitigation ? 1 : 0;
      return sev + mit;
    };
    return score(a) - score(b);
  });

  // When the parent already provides a collapse layer, render the full list
  // with no chrome (no chevron, no preview, no toggle).
  if (alwaysExpanded) {
    return (
      <View>
        {sorted.map((risk, i) => (
          <RiskCard key={`risk-${i}`} risk={risk} colors={colors} />
        ))}
      </View>
    );
  }

  const topRisk = sorted[0];
  const hasMore = sorted.length > 1;
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <Pressable
      onPress={hasMore ? () => setExpanded((v) => !v) : undefined}
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.medium,
        borderRadius: 12,
        padding: 14,
        marginBottom: 4,
      }}
    >
      {hasMore && (
        <View
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        >
          <Chevron size={16} color={colors.text.tertiary} />
        </View>
      )}

      {!expanded ? (
        <View style={{ paddingRight: hasMore ? 24 : 0 }}>
          <RiskCard risk={topRisk} colors={colors} />
          {hasMore && (
            <Text
              style={{
                color: colors.text.tertiary,
                fontSize: 11,
                fontWeight: '600',
              }}
            >
              + {sorted.length - 1} more risk{sorted.length - 1 !== 1 ? 's' : ''}
            </Text>
          )}
        </View>
      ) : (
        <View style={{ paddingRight: 24 }}>
          {sorted.map((risk, i) => (
            <RiskCard key={`risk-${i}`} risk={risk} colors={colors} />
          ))}
        </View>
      )}
    </Pressable>
  );
}
